import React, { useState, useEffect } from 'react';
import { Lock, Clock, BarChart } from 'lucide-react';
import logoAsset from '../assets/logo-asset.png';

interface HeroSectionProps {
  className?: string;
}

interface CoinData {
  price: number;
  change24h: number;
}

const DEFAULT_COINS: Record<string, CoinData> = {
  eth: { price: 3824.50, change24h: 5.23 },
  btc: { price: 98420.00, change24h: 7.68 },
  sol: { price: 185.32, change24h: 12.45 },
  ada: { price: 0.524, change24h: -2.15 }
};

const HeroSection: React.FC<HeroSectionProps> = ({ className }) => {
  const [coins, setCoins] = useState<Record<string, CoinData>>(DEFAULT_COINS);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana,cardano&vs_currencies=usd&include_24hr_change=true');
        if (!response.ok) throw new Error('CoinGecko failed');
        const data = await response.json();
        
        setCoins({
          eth: {
            price: data.ethereum?.usd ?? DEFAULT_COINS.eth.price,
            change24h: data.ethereum?.usd_24h_change ?? DEFAULT_COINS.eth.change24h
          },
          btc: {
            price: data.bitcoin?.usd ?? DEFAULT_COINS.btc.price,
            change24h: data.bitcoin?.usd_24h_change ?? DEFAULT_COINS.btc.change24h
          },
          sol: {
            price: data.solana?.usd ?? DEFAULT_COINS.sol.price,
            change24h: data.solana?.usd_24h_change ?? DEFAULT_COINS.sol.change24h
          },
          ada: {
            price: data.cardano?.usd ?? DEFAULT_COINS.ada.price,
            change24h: data.cardano?.usd_24h_change ?? DEFAULT_COINS.ada.change24h
          }
        });
      } catch (err) {
        console.warn('Failed to fetch real-time prices from CoinGecko, trying fallback:', err);
        try {
          const fallbackRes = await fetch('https://min-api.cryptocompare.com/data/pricemultifull?fsyms=BTC,ETH,SOL,ADA&tsyms=USD');
          if (fallbackRes.ok) {
            const fbData = await fallbackRes.json();
            const raw = fbData.RAW || {};
            setCoins({
              eth: {
                price: raw.ETH?.USD?.PRICE ?? DEFAULT_COINS.eth.price,
                change24h: raw.ETH?.USD?.CHANGEPCT24HOUR ?? DEFAULT_COINS.eth.change24h
              },
              btc: {
                price: raw.BTC?.USD?.PRICE ?? DEFAULT_COINS.btc.price,
                change24h: raw.BTC?.USD?.CHANGEPCT24HOUR ?? DEFAULT_COINS.btc.change24h
              },
              sol: {
                price: raw.SOL?.USD?.PRICE ?? DEFAULT_COINS.sol.price,
                change24h: raw.SOL?.USD?.CHANGEPCT24HOUR ?? DEFAULT_COINS.sol.change24h
              },
              ada: {
                price: raw.ADA?.USD?.PRICE ?? DEFAULT_COINS.ada.price,
                change24h: raw.ADA?.USD?.CHANGEPCT24HOUR ?? DEFAULT_COINS.ada.change24h
              }
            });
          }
        } catch (fallbackErr) {
          console.warn('Fallback price fetch failed, using default hardcoded prices:', fallbackErr);
        }
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: val < 1 ? 4 : 2,
      maximumFractionDigits: val < 1 ? 4 : 2
    }).format(val);
  };

  const getPnlAndPercent = (val: number, pct: number) => {
    const pnl = val * (pct / 100);
    const sign = pnl >= 0 ? '+' : '';
    const formattedPnl = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Math.abs(pnl) < 1 ? 4 : 2,
      maximumFractionDigits: Math.abs(pnl) < 1 ? 4 : 2
    }).format(pnl);
    
    return {
      pnlStr: `${sign}${formattedPnl}`,
      pctStr: `${sign}${pct.toFixed(2)}%`,
      isPositive: pnl >= 0
    };
  };

  const ethData = getPnlAndPercent(coins.eth.price, coins.eth.change24h);
  const btcData = getPnlAndPercent(coins.btc.price, coins.btc.change24h);
  const solData = getPnlAndPercent(coins.sol.price, coins.sol.change24h);
  const adaData = getPnlAndPercent(coins.ada.price, coins.ada.change24h);

  return (
    <div className={className || "hidden lg:flex flex-col justify-center p-12 bg-neutral-950 relative overflow-hidden border-r border-neutral-800 lg:w-[55%] xl:w-[58%]"}>
      {/* Abstract Background Accents */}
      <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-red-900 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-1/4 w-[400px] h-[400px] bg-blue-900 rounded-full blur-[100px] opacity-30" />
      </div>

      <div className="relative z-10">
        <div className="mb-4 opacity-20">
          <img 
            src={logoAsset} 
            alt="Catat Crypto" 
            className="h-32 w-auto object-contain"
          />
        </div>
        <h2 className="text-4xl font-medium text-white mb-6 tracking-tight">The Opportunity is You</h2>
        
        {/* Visual Accent - Mocking the screenshots */}
        <div className="flex gap-4 mt-12 relative">
          {/* Glow shapes behind cards */}
          <div className="absolute -top-12 left-8 w-32 h-32 bg-red-500 rounded-full blur-[80px] opacity-40 pointer-events-none" />
          <div className="absolute -top-8 right-16 w-24 h-24 bg-pink-500 rounded-full blur-[60px] opacity-30 pointer-events-none" />
          <div className="absolute -bottom-16 left-16 w-40 h-40 bg-pink-600 rounded-full blur-[100px] opacity-35 pointer-events-none" />
          <div className="absolute -bottom-12 right-8 w-32 h-32 bg-red-600 rounded-full blur-[80px] opacity-40 pointer-events-none" />
          
          <div className="w-48 h-64 bg-neutral-900/40 backdrop-blur-xl border border-white/5 rounded-3xl p-6 flex flex-col justify-end relative overflow-visible shadow-[0_20px_60px_-15px_rgba(239,68,68,0.5),0_10px_30px_-10px_rgba(236,72,153,0.3)]">
            <div className="absolute top-6 left-6 w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-500/20 shadow-inner">
              <img src="https://raw.githubusercontent.com/spothq/cryptocurrency-icons/master/128/color/eth.png" alt="ETH" className="w-8 h-8 rounded-full object-contain shrink-0" />
            </div>
            <div className="space-y-1 font-sans">
              <p className="text-white font-bold opacity-80 uppercase text-[10px] tracking-widest">Ethereum</p>
              <p className="text-white text-lg font-black leading-none">ETH <span className="text-[10px] opacity-40 font-normal">USD</span></p>
              <div className="h-[1px] w-full bg-white/10 my-2" />
              <p className="text-white font-mono text-sm font-bold">{formatPrice(coins.eth.price)}</p>
              <div className="flex items-center justify-between mt-1">
                <span className={`font-bold text-[10px] font-mono ${ethData.isPositive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {ethData.pnlStr}
                </span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border font-mono ${
                  ethData.isPositive 
                    ? 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20' 
                    : 'text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/20'
                }`}>
                  {ethData.pctStr}
                </span>
              </div>
            </div>
          </div>
          
          <div className="w-48 h-64 bg-neutral-900/40 backdrop-blur-xl border border-white/5 rounded-3xl p-6 flex flex-col justify-end translate-y-8 relative overflow-visible shadow-[0_20px_60px_-15px_rgba(239,68,68,0.5),0_10px_30px_-10px_rgba(236,72,153,0.3)]">
            <div className="absolute top-6 left-6 w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center border border-amber-500/20 shadow-inner">
              <img src="https://raw.githubusercontent.com/spothq/cryptocurrency-icons/master/128/color/btc.png" alt="BTC" className="w-8 h-8 rounded-full object-contain shrink-0" />
            </div>
            <div className="space-y-1 font-sans">
              <p className="text-white font-bold opacity-80 uppercase text-[10px] tracking-widest">Bitcoin</p>
              <p className="text-white text-lg font-black leading-none">BTC <span className="text-[10px] opacity-40 font-normal">USD</span></p>
              <div className="h-[1px] w-full bg-white/10 my-2" />
              <p className="text-white font-mono text-sm font-bold">{formatPrice(coins.btc.price)}</p>
              <div className="flex items-center justify-between mt-1">
                <span className={`font-bold text-[10px] font-mono ${btcData.isPositive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {btcData.pnlStr}
                </span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border font-mono ${
                  btcData.isPositive 
                    ? 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20' 
                    : 'text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/20'
                }`}>
                  {btcData.pctStr}
                </span>
              </div>
            </div>
          </div>

          <div className="w-48 h-64 bg-neutral-900/40 backdrop-blur-xl border border-white/5 rounded-3xl p-6 flex flex-col justify-end translate-y-4 relative overflow-visible shadow-[0_20px_60px_-15px_rgba(239,68,68,0.5),0_10px_30px_-10px_rgba(236,72,153,0.3)]">
            <div className="absolute top-6 left-6 w-12 h-12 bg-purple-500/10 rounded-2xl flex items-center justify-center border border-purple-500/20 shadow-inner">
              <img src="https://raw.githubusercontent.com/spothq/cryptocurrency-icons/master/128/color/sol.png" alt="SOL" className="w-8 h-8 rounded-full object-contain shrink-0" />
            </div>
            <div className="space-y-1 font-sans">
              <p className="text-white font-bold opacity-80 uppercase text-[10px] tracking-widest">Solana</p>
              <p className="text-white text-lg font-black leading-none">SOL <span className="text-[10px] opacity-40 font-normal">USD</span></p>
              <div className="h-[1px] w-full bg-white/10 my-2" />
              <p className="text-white font-mono text-sm font-bold">{formatPrice(coins.sol.price)}</p>
              <div className="flex items-center justify-between mt-1">
                <span className={`font-bold text-[10px] font-mono ${solData.isPositive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {solData.pnlStr}
                </span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border font-mono ${
                  solData.isPositive 
                    ? 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20' 
                    : 'text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/20'
                }`}>
                  {solData.pctStr}
                </span>
              </div>
            </div>
          </div>

          <div className="w-48 h-64 bg-neutral-900/40 backdrop-blur-xl border border-white/5 rounded-3xl p-6 flex flex-col justify-end translate-y-12 relative overflow-visible shadow-[0_20px_60px_-15px_rgba(239,68,68,0.5),0_10px_30px_-10px_rgba(236,72,153,0.3)]">
            <div className="absolute top-6 left-6 w-12 h-12 bg-cyan-500/10 rounded-2xl flex items-center justify-center border border-cyan-500/20 shadow-inner">
              <img src="https://raw.githubusercontent.com/spothq/cryptocurrency-icons/master/128/color/ada.png" alt="ADA" className="w-8 h-8 rounded-full object-contain shrink-0" />
            </div>
            <div className="space-y-1 font-sans">
              <p className="text-white font-bold opacity-80 uppercase text-[10px] tracking-widest">Cardano</p>
              <p className="text-white text-lg font-black leading-none">ADA <span className="text-[10px] opacity-40 font-normal">USD</span></p>
              <div className="h-[1px] w-full bg-white/10 my-2" />
              <p className="text-white font-mono text-sm font-bold">{formatPrice(coins.ada.price)}</p>
              <div className="flex items-center justify-between mt-1">
                <span className={`font-bold text-[10px] font-mono ${adaData.isPositive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                  {adaData.pnlStr}
                </span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border font-mono ${
                  adaData.isPositive 
                    ? 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20' 
                    : 'text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/20'
                }`}>
                  {adaData.pctStr}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-12 left-12 flex gap-8">
         <div className="flex items-center gap-2 text-white/40 text-xs uppercase tracking-widest font-bold">
           <Lock size={12} className="text-red-600" /> Secure
         </div>
         <div className="flex items-center gap-2 text-white/40 text-xs uppercase tracking-widest font-bold">
           <Clock size={12} className="text-red-600" /> Fast
         </div>
         <div className="flex items-center gap-2 text-white/40 text-xs uppercase tracking-widest font-bold">
           <BarChart size={12} className="text-red-600" /> Analysis
         </div>
      </div>
    </div>
  );
};

export default HeroSection;
